import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

// ─── Core Page ───────────────────────────────────────────────────────────────

const corePrinciples = [
  {
    icon: 'ri-leaf-line',
    title: '自然素材の厳選',
    desc: 'すべての原料に完全な産地証明を求め、食品安全基準を厳守します。品質の保証はすべて源から始まります。',
    color: '#4CAF50',
  },
  {
    icon: 'ri-eye-line',
    title: '安心と透明性',
    desc: '原料の選定・処方設計・製造プロセスに至るまで、すべてをオープンに。クライアントと消費者に真の信頼を届けます。',
    color: '#43a047',
  },
  {
    icon: 'ri-plant-line',
    title: '長期的な安定成長',
    desc: '誇大な効果を追い求めず、身体が日常的に安定した健康を維持できる力を与えることに専念し、ブランドの長期的成長をサポートします。',
    color: '#388e3c',
  },
];

const coreBeliefs = [
  {
    num: '01',
    title: '自然に立ち返る',
    desc: '身体と本当に共生できる栄養は、自然の本質に立ち返らなければなりません。最も純粋な力は大地から生まれると信じています。',
  },
  {
    num: '02',
    title: '品質は原料から始まる',
    desc: 'トレーサビリティが確認された、食品安全基準に適合した天然素材を厳選し、すべての信頼を透明で安心できる基盤の上に築きます。',
  },
  {
    num: '03',
    title: 'コンサルティング型パートナー',
    desc: '康永生技は専門的なコンサルタントとして、市場の洗礼と法規の審査に耐えられる製品づくりをブランドとともに歩みます。',
  },
];

function CorePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=serene%20natural%20landscape%20with%20lush%20green%20forest%20misty%20mountains%20at%20dawn%2C%20pure%20clean%20water%20stream%20flowing%20through%20rocks%2C%20tranquil%20zen%20atmosphere%2C%20soft%20natural%20morning%20light%2C%20botanical%20wellness%20philosophy%20background&width=1920&height=440&seq=core-hero-bg-01&orientation=landscape"
            alt="理念背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/88 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'ホーム', href: '/ja' }, { label: '理念' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              ホーム · 理念
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              理念
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              自然を基盤として初めて、永続する健康を生み出せる。
            </p>
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-[#4CAF50] py-6">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
          {[
            { icon: 'ri-leaf-line', text: '天然原料の厳格なトレーサビリティ' },
            { icon: 'ri-shield-check-line', text: '全工程の食品安全基準準拠' },
            { icon: 'ri-heart-line', text: '消費者の健康を守る責任' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-white text-xl`} />
              </div>
              <span className="text-white font-semibold text-[1rem]">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Brand belief intro */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '440px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=close-up%20of%20natural%20herbal%20ingredients%20arranged%20beautifully%20on%20clean%20white%20surface%2C%20various%20botanical%20roots%20seeds%20leaves%20flowers%2C%20health%20supplement%20ingredients%20flat%20lay%2C%20warm%20natural%20lighting%20minimal%20style&width=800&height=440&seq=core-intro-img-01&orientation=landscape"
                alt="康永生技 理念 天然素材"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-6 bg-white/95 rounded-xl px-5 py-3 border border-green-100">
                <span className="text-[#4CAF50] font-bold text-[0.9375rem]">20年以上の天然原料研究実績</span>
              </div>
            </div>
            <div>
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                ブランド理念
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-6">
                自然に立ち返ることこそが、根本の道
              </h2>
              <div className="space-y-4 text-gray-500 text-[1.0625rem] leading-[1.85]">
                <p>
                  康永生技は、身体と本当に共生できる栄養は自然の本質に立ち返らなければならないと信じています。私たちは源から品質管理を徹底し、産地証明のある食品安全基準適合の天然素材を選定することで、すべての信頼を透明で安心できる基盤の上に築いています。
                </p>
                <p>
                  自然界の素材にはそれぞれ価値があります。健康食品の使命は誇大な効果を追求することではなく、身体が日常的に安定を維持できる力を与えることにあります。だから康永生技は専門的なコンサルタントとして、市場と法規の両方に耐えられる製品づくりをクライアントと共に歩み、この永続する健康を守り続けます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three principles cards */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              3つのコア理念
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              私たちがすべてにおいて貫くこと
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              原料の選定・製造・提携の方法まで、3つのコア理念が康永生技のすべての側面に貫かれています
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {corePrinciples.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:-translate-y-1 hover:border-green-200 transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-green-50 border border-green-100 mb-6">
                  <i className={`${p.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.1875rem] mb-3">{p.title}</h3>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.8]">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Belief steps */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                私たちの信念
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-8">
                クライアントとともに永続する健康を守る
              </h2>
              <div className="space-y-7">
                {coreBeliefs.map((b) => (
                  <div key={b.num} className="flex gap-5">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-green-50 rounded-full border border-green-200">
                      <span className="text-[#4CAF50] font-bold text-[0.875rem]">{b.num}</span>
                    </div>
                    <div>
                      <h4 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{b.title}</h4>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '420px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20biotech%20laboratory%20researcher%20examining%20natural%20plant%20extract%20samples%20under%20microscope%2C%20clean%20white%20lab%20environment%20with%20green%20plants%20visible%2C%20health%20supplement%20quality%20control%20process%2C%20warm%20lighting&width=800&height=420&seq=core-lab-img-01&orientation=landscape"
                alt="康永生技 研究チーム"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2310]/40 to-transparent" />
              <div className="absolute bottom-6 right-6 bg-white/95 rounded-xl px-5 py-3 border border-green-100">
                <span className="text-[#4CAF50] font-bold text-[0.9375rem]">専門コンサルタントR&Dチーム</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            一緒に本当に良い製品を作りましょう
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            康永生技の天然素材への理念とコンサルティングサービスで、ブランドをより遠くへ、より長く導きます。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ja/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              ODM提携についてお問い合わせ
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/ja/home/advantages"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              康永の強みを見る
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '康永の強み', desc: '天然素材・統合サービス・小ロット対応における4つのコアアドバンテージをご紹介。', href: '/ja/home/advantages' },
        { title: '提携フロー', desc: 'ニーズの確認から納品完了まで、康永との各ステップを明確に把握できます。', href: '/ja/cooperation/process' },
        { title: 'お問い合わせ', desc: 'お問い合わせフォームを送信いただければ、1営業日以内に担当者よりご連絡します。', href: '/ja/contact/form' },
      ]} />
    </>
  );
}

// ─── Advantages Page ──────────────────────────────────────────────────────────

const advantagesJa = [
  {
    icon: 'ri-leaf-line',
    num: '01',
    title: '自然素材へのこだわり',
    desc: '化学的な添加物を排除し、天然食品由来の原料のみを厳選しています。すべての原料に完全な産地証明と素材証明書を求め、製品の品質と安全性を保証します。',
    highlights: ['完全なトレーサビリティ', '化学添加物不使用', '食品安全基準適合', '原料素材証明'],
    imgSrc: 'https://readdy.ai/api/search-image?query=natural%20organic%20botanical%20ingredients%20arranged%20on%20white%20surface%2C%20various%20seeds%20herbs%20roots%20vitamins%20capsules%2C%20clean%20minimal%20food%20photography%2C%20health%20supplement%20raw%20materials%20top%20view&width=700&height=420&seq=adv-img-01&orientation=landscape',
  },
  {
    icon: 'ri-group-line',
    num: '02',
    title: '三方ウィン・ウィン・ウィンの実現',
    desc: '康永はクライアントと競合しません。ブランドの安定した市場参入・消費者の安心な摂取・製造の品質維持に専念し、三者にとってよい循環を生み出します。',
    highlights: ['ブランドの安定参入', '消費者の安心補充', '製造品質の維持', '三方よい循環'],
    imgSrc: 'https://readdy.ai/api/search-image?query=collaborative%20business%20partners%20shaking%20hands%20in%20bright%20modern%20office%2C%20health%20biotech%20startup%20team%20meeting%2C%20positive%20cooperation%20concept%2C%20green%20plants%20in%20background%2C%20natural%20warm%20light&width=700&height=420&seq=adv-img-02&orientation=landscape',
  },
  {
    icon: 'ri-tools-line',
    num: '03',
    title: '統合型プロフェッショナルサービス',
    desc: 'ニーズ分析・処方設計・剤型選択・ブランドビジュアル・法規対応・製造を一元化し、複雑な製品開発プロセスを管理可能なステップに分解します。コミュニケーションコストと試行錯誤リスクを大幅に低減します。',
    highlights: ['ワンストップ統合', '処方・剤型設計', '法規コンプライアンス', '試行錯誤リスク低減'],
    imgSrc: 'https://readdy.ai/api/search-image?query=professional%20team%20analyzing%20health%20supplement%20product%20development%20workflow%20on%20large%20screen%2C%20modern%20biotech%20company%20office%2C%20organized%20project%20planning%20board%2C%20clean%20white%20workspace%20with%20natural%20light&width=700&height=420&seq=adv-img-03&orientation=landscape',
  },
  {
    icon: 'ri-scales-line',
    num: '04',
    title: '小ロット受注対応',
    desc: '柔軟な生産オプションを提供し、新ブランドやニッチ市場の在庫リスクと資金プレッシャーを軽減。プレッシャーなく市場テストが行えます。',
    highlights: ['柔軟なMOQ', '新ブランド向け', '在庫リスク低減', '迅速な市場テスト'],
    imgSrc: 'https://readdy.ai/api/search-image?query=small%20batch%20health%20supplement%20production%20line%20in%20clean%20white%20GMP%20factory%2C%20neatly%20arranged%20capsule%20bottles%20and%20packaging%2C%20precise%20manufacturing%20process%2C%20minimal%20clinical%20aesthetic&width=700&height=420&seq=adv-img-04&orientation=landscape',
  },
];

const advStatsJa = [
  { value: '350+', label: 'ブランドパートナー数' },
  { value: '1000+', label: '開発製品数' },
  { value: '20+', label: '専門年数' },
  { value: '95%', label: 'クライアント満足率' },
];

function AdvantagesPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20biotech%20health%20supplement%20company%20building%20exterior%20with%20green%20living%20wall%20plants%2C%20professional%20corporate%20headquarters%20surrounded%20by%20nature%2C%20clean%20architecture%20daylight&width=1920&height=440&seq=adv-hero-bg-01&orientation=landscape"
            alt="康永の強み 背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'ホーム', href: '/ja' }, { label: '康永の強み' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              ホーム · 康永の強み
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              康永の強み
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              天然素材の選定・ブランド提携・生産統合・小ロット対応を通じて、ブランドの参入障壁と市場リスクを低減します。
            </p>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {advStatsJa.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-[#4CAF50] font-bold text-[2rem] md:text-[2.25rem] leading-none mb-1">{s.value}</div>
                <div className="text-gray-500 text-[0.875rem]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages alternating */}
      <section className="py-6 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-6 py-10">
          {advantagesJa.map((adv, idx) => (
            <div
              key={adv.num}
              className={`bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
            >
              <div className="lg:w-[44%] flex-shrink-0" style={{ minHeight: '300px' }}>
                <img src={adv.imgSrc} alt={adv.title} className="w-full h-full object-cover object-top" style={{ minHeight: '260px' }} />
              </div>
              <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-green-50 border border-green-100">
                    <i className={`${adv.icon} text-[#4CAF50] text-xl`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-30">{adv.num}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.5rem] md:text-[1.75rem] mb-4">{adv.title}</h3>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.85] mb-6">{adv.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {adv.highlights.map((h) => (
                    <span key={h} className="inline-flex items-center gap-1.5 text-[0.8125rem] text-[#4CAF50] bg-green-50 border border-green-100 px-3 py-1 rounded-full">
                      <i className="ri-check-line text-xs" />
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
            なぜ康永を選ぶのか
          </span>
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
            単なる工場ではなく、ブランドの生技パートナー
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-12">
            康永と一般的な受託製造業者の最大の違いは、私たちが一つひとつの提携を長期的なパートナーシップとして捉え、単に注文をこなすだけではないという点です。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-user-voice-line', title: '専任アカウントサービス', desc: '初回ご相談から納品後のフォローアップまで、専任担当者がすべて一貫してお客様のプロジェクトを担当します' },
              { icon: 'ri-file-shield-line', title: 'NDA秘密保持', desc: '厳格な秘密保持契約を締結し、お客様の処方アイデアが完全に保護されることをお約束します' },
              { icon: 'ri-building-2-line', title: 'GMP工場での生産', desc: '台中のGMP適合工場で、すべてのバッチに厳格な品質検査を実施します' },
              { icon: 'ri-scales-2-line', title: '法規コンプライアンス審査', desc: '食品・健康食品の広告表現規則に精通し、適法な市場投入をサポートします' },
              { icon: 'ri-refresh-line', title: '迅速な対応力', desc: '小ロットテスト・迅速なサンプル・柔軟な調整で、市場機会を素早くつかみます' },
              { icon: 'ri-bar-chart-line', title: '市場トレンドの洞察', desc: '健康食品市場の最新トレンドを把握し、ブランドが正しい方向を選ぶサポートをします' },
            ].map((item) => (
              <div key={item.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 text-left hover:border-green-200 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h4 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.title}</h4>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            アイデアをお持ちください。あとはお任せください
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            康永生技は初回無料評価を提供し、最小コストで最初の健康食品ブランドを立ち上げる方法をご提案します。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ja/cooperation/process"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              提携フローを確認する
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/ja/home/experience"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              サービス実績を見る
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '理念', desc: '康永生技のブランド信念：自然に立ち返ることが正しい健康の道。', href: '/ja/home/core' },
        { title: '専門製造', desc: '康永のGMP工場と品質管理プロセスについて詳しく知る。', href: '/ja/about/production' },
        { title: '製造サービスのお問い合わせ', desc: 'お問い合わせフォームを送信し、最適な提携方法を評価してもらいましょう。', href: '/ja/contact/form' },
      ]} />
    </>
  );
}

// ─── Experience Page ──────────────────────────────────────────────────────────

interface CountUpProps {
  target: number;
  suffix?: string;
  duration?: number;
}

function CountUp({ target, suffix = '', duration = 2000 }: CountUpProps) {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          const startTime = performance.now();
          const step = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const milestonesJa = [
  { year: '2003', title: '康永生技を創立', desc: 'ベテランの食品研究開発専門家が設立。天然健康食品受託製造の分野に特化' },
  { year: '2008', title: 'GMP工場認証取得', desc: 'GMP認証を正式取得。製造規範を全面的にアップグレード' },
  { year: '2013', title: '提携ブランド100社突破', desc: '提携ブランド数が100社を超え、包括的なODMサービス体制を確立' },
  { year: '2018', title: 'スマート管理システム導入', desc: 'ERPシステムを導入し、生産追跡と品質管理能力を大幅に向上' },
  { year: '2022', title: '350以上のブランドパートナーへ', desc: '累計提携ブランドが350社以上に到達。EC・薬局・チェーン店など多様な業態に対応' },
];

const categoryExpJa = [
  { icon: 'ri-heart-pulse-line', label: '美容・スキンケア', count: '80+', desc: '美肌・保湿・抗酸化シリーズ' },
  { icon: 'ri-basketball-line', label: 'スポーツ・フィットネス', count: '65+', desc: '筋力補充・体力維持シリーズ' },
  { icon: 'ri-leaf-line', label: '女性の健康', count: '90+', desc: '女性向け健康・更年期調整シリーズ' },
  { icon: 'ri-mental-health-line', label: '睡眠・ストレスケア', count: '55+', desc: '睡眠補助・神経調整シリーズ' },
  { icon: 'ri-seedling-line', label: '腸活・消化', count: '70+', desc: 'プロバイオティクス・消化サポートシリーズ' },
  { icon: 'ri-star-line', label: '子どもの成長', count: '40+', desc: '子どもカルシウム・学習力サポートシリーズ' },
];

const expTestimonialsJa = [
  {
    name: '王様 ブランド創設者',
    company: 'ECサイト健康食品ブランド',
    text: '康永は処方からパッケージまで全面サポートしてくれ、私のブランドがゼロから6ヶ月でオンライン販売開始できました。試行錯誤のコストを大幅に節約できました。',
    tag: 'ECブランド',
  },
  {
    name: '李様 ディレクター',
    company: 'チェーン薬局バイヤー',
    text: '康永との取引は長年にわたります。特に法規への精通度を高く評価しています。どの製品も棚入れ審査を問題なく通過できています。',
    tag: '薬局チャネル',
  },
  {
    name: '陳様 代表取締役',
    company: '健康食品ブランドグループ',
    text: '小ロット対応が本当に助かっています。テスト期間に大量の在庫を抱える必要がなく、売れ行きが確認できてから増産できます。非常に柔軟です。',
    tag: 'マルチブランドグループ',
  },
];

function ExperiencePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=successful%20health%20supplement%20brand%20launch%20celebration%2C%20diverse%20business%20team%20in%20modern%20office%2C%20product%20achievement%20wall%20with%20multiple%20award%20certificates%2C%20green%20accents%20corporate%20environment%2C%20natural%20lighting&width=1920&height=440&seq=exp-hero-bg-01&orientation=landscape"
            alt="サービス実績 背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'ホーム', href: '/ja' }, { label: 'サービス実績' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              ホーム · サービス実績
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              サービス実績
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              安定したサービス実績でブランドのアイデアから市場参入まで伴走します。
            </p>
          </div>
        </div>
      </section>

      {/* Key stats */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              主要指標
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              数字の背後にあるのは一つひとつの信頼
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              20年にわたる健康食品受託製造の深い専門知識を、数字で証明します
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { target: 350, suffix: '+', label: '提携サービスブランド数', desc: 'EC・薬局・直販・チェーン店など多様なブランドに対応', icon: 'ri-store-2-line' },
              { target: 1000, suffix: '+', label: '累計製造製品数', desc: '美容・スポーツ・腸活など幅広いカテゴリーにわたる', icon: 'ri-flask-line' },
              { target: 95, suffix: '%', label: 'クライアント満足率', desc: '長年の提携ブランドからの継続的な信頼と口コミに基づく実際の評価', icon: 'ri-star-smile-line' },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#f7faf7] rounded-2xl p-8 border border-gray-100 text-center hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-2xl border border-green-100 mx-auto mb-5">
                  <i className={`${stat.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <div className="text-[#4CAF50] font-bold text-[3rem] md:text-[3.5rem] leading-none mb-2">
                  <CountUp target={stat.target} suffix={stat.suffix} />
                </div>
                <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{stat.label}</div>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category experience */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              製品カテゴリー経験
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              多様な健康カテゴリーに対応
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              豊富なクロスカテゴリー開発経験により、どのような市場ポジショニングにも最適なソリューションを提供できます
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {categoryExpJa.map((cat) => (
              <div key={cat.label} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl border border-green-100 flex-shrink-0">
                    <i className={`${cat.icon} text-[#4CAF50] text-xl`} />
                  </div>
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[1rem]">{cat.label}</div>
                    <div className="text-[#4CAF50] font-bold text-[0.875rem]">{cat.count} 製品</div>
                  </div>
                </div>
                <p className="text-gray-500 text-[0.875rem] leading-[1.7]">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              沿革
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              20年の着実な成長の軌跡
            </h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-100 -translate-x-1/2" />
            <div className="space-y-8 md:space-y-0">
              {milestonesJa.map((m, idx) => (
                <div key={m.year} className={`flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                    <div className={`bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300 ${idx % 2 === 0 ? 'md:ml-auto md:max-w-md' : 'md:mr-auto md:max-w-md'}`}>
                      <div className="text-[#4CAF50] font-bold text-[1.375rem] mb-2">{m.year}</div>
                      <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{m.title}</div>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.7]">{m.desc}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-16 flex-shrink-0 items-center justify-center z-10">
                    <div className="w-4 h-4 rounded-full bg-[#4CAF50] border-4 border-white ring-2 ring-green-200" />
                  </div>
                  <div className="md:w-[calc(50%-2rem)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              ブランドの声
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              提携ブランドからの生の声
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {expTestimonialsJa.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.85] mb-6 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[0.9375rem]">{t.name}</div>
                    <div className="text-gray-400 text-[0.8125rem]">{t.company}</div>
                  </div>
                  <span className="text-[0.75rem] text-[#4CAF50] bg-green-50 border border-green-100 px-2.5 py-1 rounded-full">{t.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            次の成功事例になりませんか
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            新ブランドの立ち上げを目指す方も、拡張を検討している成熟企業も、康永なら最適な提携ソリューションをご用意しています。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ja/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              製造サービスについてお問い合わせ
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/ja/about/cases"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              成功事例を見る
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '成功事例', desc: '康永がブランドをゼロから市場参入まで導いた実際の事例をご覧ください。', href: '/ja/about/cases' },
        { title: '提携フロー', desc: 'ニーズの確認から納品完了まで、明確で管理しやすい提携ステップをご紹介。', href: '/ja/cooperation/process' },
        { title: 'お問い合わせ', desc: 'お問い合わせフォームを送信し、次の成功パートナーになりましょう。', href: '/ja/contact/form' },
      ]} />
    </>
  );
}

// ─── Hot Formulas Page ────────────────────────────────────────────────────────

const formulasJa = [
  {
    id: 'crystal-bright',
    icon: 'ri-eye-line',
    title: 'アイブライト処方',
    tag: '目の健康',
    desc: 'ルテイン・ゼアキサンチン複合処方。長時間のスクリーン使用者の視力健康をサポートします。',
    dosages: ['ソフトジェル', '錠剤', '粉末'],
    targets: ['オフィスワーカー', '学生', 'シニア層'],
    color: 'bg-green-50 border-green-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=eye%20health%20supplement%20capsules%20with%20marigold%20flowers%20lutein%20zeaxanthin%20ingredients%20on%20clean%20white%20surface%2C%20minimal%20health%20product%20photography&width=400&height=260&seq=hf-01&orientation=landscape',
  },
  {
    id: 'womens',
    icon: 'ri-women-line',
    title: '女性専用処方',
    tag: '女性の健康',
    desc: '女性の健康・ホルモンバランス・美容補充など、女性のライフステージ全般に対応した処方です。',
    dosages: ['カプセル', '錠剤', 'ゼリースティック'],
    targets: ['妊娠適齢期女性', '更年期女性', '美容重視層'],
    color: 'bg-rose-50 border-rose-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=womens%20health%20supplement%20products%20with%20rose%20flower%20petals%20and%20botanical%20ingredients%2C%20feminine%20health%20care%20products%20flat%20lay%20on%20white%20background%2C%20natural%20pink%20tones&width=400&height=260&seq=hf-02&orientation=landscape',
  },
  {
    id: 'mens',
    icon: 'ri-men-line',
    title: '男性専用処方',
    tag: '男性の活力',
    desc: '活力補充・前立腺ケア・体力強化など、現代男性のために設計された健康処方です。',
    dosages: ['カプセル', '錠剤', '粉末'],
    targets: ['中高年男性', 'アクティブライフ', '高ストレス層'],
    color: 'bg-sky-50 border-sky-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=mens%20health%20supplement%20capsules%20with%20zinc%20selenium%20saw%20palmetto%20botanical%20ingredients%20on%20clean%20white%20surface%2C%20masculine%20health%20product%20flat%20lay&width=400&height=260&seq=hf-03&orientation=landscape',
  },
  {
    id: 'gut',
    icon: 'ri-seedling-line',
    title: '腸活処方',
    tag: '消化器の健康',
    desc: '多菌種プロバイオティクス複合処方と食物繊維で腸内フローラのバランスを整え、消化を改善します。',
    dosages: ['カプセル', 'パウダーパック', '錠剤'],
    targets: ['全年齢層', '消化トラブル', '低食物繊維食'],
    color: 'bg-green-50 border-green-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=probiotic%20gut%20health%20supplement%20powder%20capsules%20with%20fermented%20ingredients%20prebiotics%20fiber%20on%20clean%20white%20surface%2C%20health%20food%20photography%20minimal&width=400&height=260&seq=hf-04&orientation=landscape',
  },
  {
    id: 'hydration',
    icon: 'ri-drop-line',
    title: '潤い美肌処方',
    tag: '美容・スキンケア',
    desc: 'ヒアルロン酸・コラーゲン・ビタミンCの三重効果処方で、肌のうるおいと輝きをインナーから維持します。',
    dosages: ['パウダーパック', 'ゼリースティック', 'カプセル'],
    targets: ['美容重視層', '乾燥肌', 'エイジングケア'],
    color: 'bg-amber-50 border-amber-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=collagen%20hyaluronic%20acid%20beauty%20supplement%20powder%20sachets%20with%20peach%20and%20rose%20on%20white%20surface%2C%20feminine%20health%20skincare%20product%20photography&width=400&height=260&seq=hf-05&orientation=landscape',
  },
  {
    id: 'hair',
    icon: 'ri-scissors-line',
    title: 'ヘアケア処方',
    tag: '毛髪の健康',
    desc: 'ノコギリヤシ・ビオチン・システイン複合処方で、抜け毛の悩みに全方位的にアプローチします。',
    dosages: ['カプセル', '錠剤'],
    targets: ['抜け毛の悩み', '中高年男女', '産後の母親'],
    color: 'bg-orange-50 border-orange-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=hair%20health%20supplement%20biotin%20capsules%20with%20rosemary%20herbs%20saw%20palmetto%20on%20white%20surface%2C%20hair%20care%20product%20minimal%20photography%20warm%20natural%20tones&width=400&height=260&seq=hf-06&orientation=landscape',
  },
  {
    id: 'baby',
    icon: 'ri-parent-line',
    title: 'ベビー処方',
    tag: '乳幼児向け',
    desc: '乳幼児専用安心処方。DHA・プロバイオティクス・ビタミンDを優しく配合し、厳格な品質管理を実施。',
    dosages: ['粉末', 'ドロップ'],
    targets: ['0〜3歳', '授乳中の母親', '保護者選択'],
    color: 'bg-yellow-50 border-yellow-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=baby%20infant%20supplement%20powder%20formula%20DHA%20omega3%20vitamins%20on%20clean%20white%20surface%2C%20gentle%20soft%20pastel%20tones%20health%20product%20photography%20for%20infants&width=400&height=260&seq=hf-07&orientation=landscape',
  },
  {
    id: 'pets',
    icon: 'ri-heart-line',
    title: 'ペット健康処方',
    tag: 'ペットケア',
    desc: '猫・犬向けの健康処方。関節ケア・腸活・被毛の艶の3方向に対応しています。',
    dosages: ['粉末', '錠剤', 'おやつ形式'],
    targets: ['猫のオーナー', '犬のオーナー', '高齢ペット'],
    color: 'bg-amber-50 border-amber-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20products%20for%20dogs%20and%20cats%2C%20natural%20ingredient%20treats%20powder%20on%20white%20surface%2C%20veterinary%20supplement%20product%20minimal%20photography%20warm%20tones&width=400&height=260&seq=hf-08&orientation=landscape',
  },
  {
    id: 'fitness',
    icon: 'ri-run-line',
    title: 'スポーツパフォーマンス処方',
    tag: 'スポーツ・フィットネス',
    desc: 'ホエイプロテイン・BCAA・クレアチン複合処方で、運動後の迅速な回復と筋肉維持をサポートします。',
    dosages: ['粉末', 'カプセル', '錠剤'],
    targets: ['ジム愛好家', 'アスリート', 'アクティブライフ'],
    color: 'bg-emerald-50 border-emerald-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=sports%20fitness%20supplement%20protein%20powder%20capsules%20with%20green%20leaf%20botanical%20ingredients%20on%20clean%20white%20surface%2C%20athletic%20health%20product%20flat%20lay%20photography&width=400&height=260&seq=hf-09&orientation=landscape',
  },
  {
    id: 'sleep',
    icon: 'ri-moon-line',
    title: '睡眠・リラクゼーション処方',
    tag: '睡眠サポート',
    desc: 'トリプトファン・GABA・テアニンの三重作用睡眠処方で、入眠困難と睡眠の質改善をサポートします。',
    dosages: ['カプセル', '錠剤', 'ゼリースティック'],
    targets: ['不眠症', '高ストレス層', '交代制勤務者'],
    color: 'bg-indigo-50 border-indigo-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=sleep%20wellness%20supplement%20chamomile%20lavender%20valerian%20melatonin%20products%20on%20white%20surface%2C%20calming%20nighttime%20health%20product%20photography%20soft%20purple%20tones&width=400&height=260&seq=hf-10&orientation=landscape',
  },
  {
    id: 'kids',
    icon: 'ri-user-heart-line',
    title: '子どもの成長処方',
    tag: '子ども向け健康',
    desc: 'カルシウム・ビタミンD3・脳をサポートするDHAで、子どもの骨発育と学習能力をサポートします。',
    dosages: ['グミ', '粉末', '錠剤'],
    targets: ['学齢期の子ども', '偏食の子ども', '保護者選択'],
    color: 'bg-lime-50 border-lime-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20gummy%20vitamins%20calcium%20DHA%20colorful%20products%20on%20white%20surface%2C%20cute%20playful%20health%20product%20photography%20bright%20cheerful%20tones&width=400&height=260&seq=hf-11&orientation=landscape',
  },
  {
    id: 'slimming',
    icon: 'ri-scales-2-line',
    title: '体重管理処方',
    tag: '体重管理',
    desc: 'ガルシニア・白インゲン豆・カプサイシン複合処方と食物繊維で体重管理と軽やかな体型維持をサポートします。',
    dosages: ['カプセル', 'パウダーパック'],
    targets: ['体重管理志向', '低カロリー食', 'ダイエット計画'],
    color: 'bg-teal-50 border-teal-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=weight%20management%20diet%20supplement%20with%20garcinia%20cambogia%20green%20tea%20extract%20capsules%20on%20white%20surface%2C%20slim%20figure%20health%20product%20minimal%20clean%20photography&width=400&height=260&seq=hf-12&orientation=landscape',
  },
];

function HotFormulasPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20variety%20of%20health%20supplement%20products%20capsules%20powders%20gummies%20arranged%20beautifully%20on%20clean%20white%20marble%20surface%2C%20best%20selling%20nutraceutical%20product%20collection%20flat%20lay%20photography&width=1920&height=440&seq=hhf-hero-bg-01&orientation=landscape"
            alt="人気処方 背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'ホーム', href: '/ja' }, { label: '人気処方' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              ホーム · 人気処方
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              人気処方
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              市場ニーズ・ターゲット層・チャネルポジショニングに基づき、ブランドに最適な健康食品処方の方向性をサポートします。
            </p>
          </div>
        </div>
      </section>

      {/* Intro bar */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-medal-line', text: '市場実績で検証済み' },
              { icon: 'ri-flask-line', text: '多様なR&D処方' },
              { icon: 'ri-leaf-line', text: '厳選天然原料' },
              { icon: 'ri-settings-line', text: '柔軟なカスタマイズ' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg border border-green-100 flex-shrink-0">
                  <i className={`${item.icon} text-[#4CAF50] text-base`} />
                </div>
                <span className="text-[#1a2e1a] text-[0.875rem] font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formula cards */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              12大人気カテゴリー
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              市場で最も支持される処方の選択肢
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              すべての処方方向は市場需要分析に基づき、天然成分研究と組み合わせています。人気カテゴリーへの参入をスムーズにサポートします
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {formulasJa.map((f) => (
              <div
                key={f.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-green-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-full overflow-hidden" style={{ height: '170px' }}>
                  <img
                    src={f.imgSrc}
                    alt={f.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg border border-green-100">
                        <i className={`${f.icon} text-[#4CAF50] text-sm`} />
                      </div>
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem]">{f.title}</h3>
                    </div>
                    <span className="text-[0.6875rem] text-[#4CAF50] bg-green-50 border border-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">{f.tag}</span>
                  </div>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.7] mb-4">{f.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {f.dosages.map((d) => (
                      <span key={d} className="text-[0.75rem] text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">{d}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                    className="w-full flex items-center justify-between text-[0.875rem] text-[#4CAF50] font-medium cursor-pointer hover:text-[#43a047] transition-colors py-1"
                  >
                    <span>{expandedId === f.id ? '閉じる' : '詳細を見る'}</span>
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${expandedId === f.id ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedId === f.id && (
                    <div className="pt-3 border-t border-gray-100 mt-3">
                      <div className="text-[0.8125rem] text-gray-500 mb-2 font-medium">対象層：</div>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {f.targets.map((t) => (
                          <span key={t} className="text-[0.75rem] text-gray-600 bg-[#f7faf7] border border-gray-200 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                      <Link
                        to="/ja/services/hot-formulas"
                        className="w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white text-[0.875rem] font-semibold py-2.5 rounded-xl transition-all duration-300 cursor-pointer"
                      >
                        詳細な処方情報を見る
                        <i className="ri-arrow-right-line text-sm" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customization notice */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="bg-[#f7faf7] rounded-2xl p-8 md:p-12 border border-green-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                  処方カスタマイズについて
                </span>
                <h3 className="text-[#1a2e1a] font-bold text-[1.5rem] md:text-[1.875rem] mb-4">
                  以上は参考方向です。全面的なカスタマイズが可能です
                </h3>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.85]">
                  ブランドごとに市場ポジショニングは異なります。康永生技はお客様のブランド戦略・ターゲット層・予算に応じた最適な処方組み合わせをカスタマイズして提案します。
                </p>
              </div>
              <div className="space-y-3">
                {[
                  { icon: 'ri-check-line', text: '原料の代替と配合量の調整' },
                  { icon: 'ri-check-line', text: '剤型の柔軟な切り替え' },
                  { icon: 'ri-check-line', text: '特定チャネルの仕様要件への対応' },
                  { icon: 'ri-check-line', text: '法規コンプライアンスの事前確認' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#4CAF50] rounded-full flex-shrink-0">
                      <i className={`${item.icon} text-white text-xs`} />
                    </div>
                    <span className="text-[#1a2e1a] text-[1rem]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            処方の方向性は見つかりましたか？
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            康永生技の処方コンサルタントに今すぐご相談ください。市場で最も支持される健康ブランドの構築をお手伝いします。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ja/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              人気処方についてお問い合わせ
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/ja/services/hot-formulas"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              さらに詳しい処方情報を見る
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '人気処方（製造サービス）', desc: '12大人気処方の詳細説明と剤型選択をご覧ください。', href: '/ja/services/hot-formulas' },
        { title: '各種剤型', desc: 'カプセル・錠剤・粉末・ゼリースティック…ブランドに合った剤型を選ぶ。', href: '/ja/services/dosage-forms' },
        { title: '処方開発についてお問い合わせ', desc: 'お問い合わせフォームを送信し、R&Dチームと人気製品を一緒に作りましょう。', href: '/ja/contact/form' },
      ]} />
    </>
  );
}

// ─── Route Dispatcher ─────────────────────────────────────────────────────────

export default function JaHomeSubPage() {
  const location = useLocation();

  const seoMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/ja/home/core': {
      title: '理念｜自然に立ち返る健康づくり｜康永生技',
      description: '康永生技の理念：自然の本質に立ち返り、厳格な天然素材トレーサビリティを通じて、コンサルティング型パートナーシップでブランドと共に市場に耐えうる製品を構築します。',
      keywords: '康永理念,天然素材,自然健康,ブランドパートナー,健康食品理念',
    },
    '/ja/home/advantages': {
      title: '康永の強み｜天然素材・統合サービス・小ロット対応',
      description: '康永生技の4つの強み：天然素材へのこだわり、三方よしの実現、統合型プロフェッショナルサービス、小ロット受注対応。ブランドの参入障壁を下げます。',
      keywords: '康永の強み,小ロット対応,天然素材,統合サービス,健康食品OEM',
    },
    '/ja/home/experience': {
      title: 'サービス実績｜350+ブランドの信頼｜康永生技',
      description: '康永生技のサービス実績：350+ブランド、1000+開発製品、95%顧客満足率、20年の健康食品受託製造の専門知識。',
      keywords: '康永実績,健康食品製造実績,ブランドパートナー,OEMトラックレコード,受託製造経験',
    },
    '/ja/home/hot-formulas': {
      title: '人気処方｜12の健康食品処方方向｜康永生技',
      description: '12の人気処方方向：目の健康・女性向け・男性活力・腸活・美肌・髪の健康・ベビー・ペット・スポーツ・睡眠・子ども・体重管理。',
      keywords: '人気処方,健康食品処方,ODM処方,人気方向,康永生技処方',
    },
  };

  const seoData = seoMap[location.pathname] || seoMap['/ja/home/core'];
  useSEO({ ...seoData, lang: 'ja', canonicalPath: location.pathname });

  const renderPage = () => {
    switch (location.pathname) {
      case '/ja/home/core':
        return <CorePage />;
      case '/ja/home/advantages':
        return <AdvantagesPage />;
      case '/ja/home/experience':
        return <ExperiencePage />;
      case '/ja/home/hot-formulas':
        return <HotFormulasPage />;
      default:
        return (
          <section className="py-40 text-center">
            <h1 className="text-[#1a2e1a] font-bold text-[2rem]">ページが見つかりません</h1>
            <Link to="/ja" className="mt-6 inline-block text-[#4CAF50] underline">ホームに戻る</Link>
          </section>
        );
    }
  };

  return <JaLayout>{renderPage()}</JaLayout>;
}
